/**
 * FreightLaneMatrix — a WH × WH grid of per-lane freight overrides.
 *
 * Rows = source, cols = destination. Cells edit inline; save on blur.
 * Diagonal is disabled (can't ship to yourself). Blank cell = no lane
 * configured, settlement falls back to the source warehouse's rate
 * card `flat_freight_fee`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowsClockwise, Path, Info, ClockCounterClockwise, X } from '@phosphor-icons/react';
import {
  getWarehouses, listFreightLanes, saveFreightLane, deleteFreightLane, getFreightLaneHistory,
} from '../lib/api';

export default function FreightLaneMatrix() {
  const [warehouses, setWarehouses] = useState([]);
  const [lanes, setLanes] = useState({});     // "fromId:toId" → { amount, currency }
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [editing, setEditing] = useState({}); // key → in-progress string value
  const [historyFor, setHistoryFor] = useState(null); // { fromId, toId, fromCode, toCode, rows }

  const load = async () => {
    setLoading(true);
    try {
      const [w, l] = await Promise.all([getWarehouses(), listFreightLanes()]);
      setWarehouses((w.data || []).filter((x) => x.is_active));
      const map = {};
      (l.data || []).forEach((row) => {
        map[`${row.from_warehouse_id}:${row.to_warehouse_id}`] = row;
      });
      setLanes(map);
      setEditing({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load matrix');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const cellKey = (f, t) => `${f}:${t}`;

  const displayValue = (fromId, toId) => {
    const key = cellKey(fromId, toId);
    if (editing[key] !== undefined) return editing[key];
    const lane = lanes[key];
    return lane ? String(lane.amount) : '';
  };

  const commit = async (fromId, toId) => {
    const key = cellKey(fromId, toId);
    const raw = editing[key];
    if (raw === undefined) return;
    const existing = lanes[key];
    const trimmed = String(raw).trim();

    // Empty commit → delete the lane if one existed
    if (trimmed === '') {
      if (existing) {
        setSavingKey(key);
        try {
          await deleteFreightLane(fromId, toId);
          setLanes((prev) => { const copy = { ...prev }; delete copy[key]; return copy; });
          toast.success('Lane cleared');
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Could not clear lane');
        } finally { setSavingKey(null); }
      }
      setEditing((prev) => { const copy = { ...prev }; delete copy[key]; return copy; });
      return;
    }

    const amount = Number(trimmed);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error('Amount must be a positive number');
      return;
    }
    if (existing && Number(existing.amount) === amount) {
      // Nothing to save
      setEditing((prev) => { const copy = { ...prev }; delete copy[key]; return copy; });
      return;
    }
    setSavingKey(key);
    try {
      const currency = existing?.currency || warehouses.find((w) => w.id === fromId)?.currency || 'USD';
      const r = await saveFreightLane(fromId, toId, { amount, currency });
      setLanes((prev) => ({
        ...prev,
        [key]: { from_warehouse_id: fromId, to_warehouse_id: toId, amount: r.data.amount, currency: r.data.currency },
      }));
      setEditing((prev) => { const copy = { ...prev }; delete copy[key]; return copy; });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save lane');
    } finally { setSavingKey(null); }
  };

  const configuredCount = useMemo(() => Object.keys(lanes).length, [lanes]);
  const totalCells = warehouses.length > 1 ? warehouses.length * (warehouses.length - 1) : 0;

  const openHistory = async (fromWh, toWh) => {
    setHistoryFor({ fromId: fromWh.id, toId: toWh.id, fromCode: fromWh.code, toCode: toWh.code, rows: null });
    try {
      const r = await getFreightLaneHistory(fromWh.id, toWh.id);
      setHistoryFor((prev) => prev ? { ...prev, rows: r.data } : prev);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load history');
      setHistoryFor(null);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-4" data-testid="freight-matrix-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Path size={28} weight="fill" className="text-brand" /> Freight Rate Matrix
            </h1>
            <p className="text-sm text-muted-foreground">
              Per-lane freight overrides so long routes cost more than short hops. Rows = source, columns = destination.
              Type an amount and tab out to save. Empty cell = fall back to source rate card.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="matrix-refresh-btn">
            <ArrowsClockwise size={14} className="mr-1" /> Refresh
          </Button>
        </div>

        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Info size={12} weight="bold" />
          <span data-testid="matrix-configured-count">{configuredCount} / {totalCells} lanes configured</span>
          <span>· Diagonal disabled (a warehouse can&apos;t ship to itself)</span>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading matrix…</div>
        ) : warehouses.length < 2 ? (
          <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            You need at least 2 active warehouses to configure freight lanes.
          </div>
        ) : (
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-xs" data-testid="freight-matrix-table">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-2 py-2 text-[10px] uppercase tracking-widest sticky left-0 bg-muted/40">
                    From ↓ · To →
                  </th>
                  {warehouses.map((wh) => (
                    <th key={wh.id} className="px-2 py-2 text-[10px] uppercase tracking-widest text-center font-mono" title={wh.name}>
                      {wh.code}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {warehouses.map((fromWh) => (
                  <tr key={fromWh.id} className="border-b border-border/50">
                    <td className="px-2 py-1.5 font-mono font-bold sticky left-0 bg-card">
                      {fromWh.code}
                      <div className="text-[10px] text-muted-foreground font-normal">{fromWh.name}</div>
                    </td>
                    {warehouses.map((toWh) => {
                      const key = cellKey(fromWh.id, toWh.id);
                      const isDiagonal = fromWh.id === toWh.id;
                      const lane = lanes[key];
                      return (
                        <td key={toWh.id} className="px-1 py-1">
                          {isDiagonal ? (
                            <div className="text-center text-muted-foreground">—</div>
                          ) : (
                            <div className="relative">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder={savingKey === key ? '…' : ''}
                                value={displayValue(fromWh.id, toWh.id)}
                                disabled={savingKey === key}
                                onChange={(e) => setEditing((prev) => ({ ...prev, [key]: e.target.value }))}
                                onBlur={() => commit(fromWh.id, toWh.id)}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                className={`h-8 text-right font-mono text-xs w-24 ${lane ? 'border-emerald-300 bg-emerald-50/40' : ''}`}
                                data-testid={`lane-cell-${fromWh.code}-${toWh.code}`}
                              />
                              {lane && (
                                <span className="absolute -top-1 -right-1 text-[9px] text-emerald-700 font-bold" data-testid={`lane-currency-${fromWh.code}-${toWh.code}`}>
                                  {lane.currency}
                                </span>
                              )}
                              {lane && (
                                <button
                                  type="button"
                                  onClick={() => openHistory(fromWh, toWh)}
                                  className="absolute -bottom-1 -right-1 text-emerald-700 hover:text-emerald-900 opacity-70 hover:opacity-100"
                                  title="Rate history"
                                  data-testid={`lane-history-${fromWh.code}-${toWh.code}`}
                                >
                                  <ClockCounterClockwise size={12} weight="fill" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {historyFor && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setHistoryFor(null)} data-testid="lane-history-modal">
            <div className="bg-card border border-border max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-border">
                <div>
                  <h2 className="text-lg font-bold flex items-center gap-2">
                    <ClockCounterClockwise size={18} weight="fill" /> Lane rate history
                  </h2>
                  <div className="text-xs text-muted-foreground font-mono">
                    {historyFor.fromCode} → {historyFor.toCode}
                  </div>
                </div>
                <button onClick={() => setHistoryFor(null)} className="text-muted-foreground hover:text-foreground" data-testid="close-lane-history">
                  <X size={20} />
                </button>
              </div>
              <div className="p-4">
                {historyFor.rows === null ? (
                  <div className="text-sm text-muted-foreground">Loading…</div>
                ) : historyFor.rows.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No changes recorded yet.</div>
                ) : (
                  <ol className="space-y-2" data-testid="lane-history-list">
                    {historyFor.rows.map((h, i) => {
                      const delta = h.delta;
                      const isCreate = h.previous_amount == null;
                      const isIncrease = typeof delta === 'number' && delta > 0;
                      const isDecrease = typeof delta === 'number' && delta < 0;
                      return (
                        <li key={i} className="border-l-2 border-brand pl-3 py-1">
                          <div className="text-xs font-mono">
                            {new Date(h.changed_at).toLocaleString()}
                          </div>
                          <div className="text-sm mt-0.5">
                            {isCreate ? (
                              <span>Lane created at <span className="font-bold font-mono">{h.new_currency} {Number(h.new_amount).toFixed(2)}</span></span>
                            ) : (
                              <span>
                                <span className="font-mono line-through opacity-60">{h.previous_currency || h.new_currency} {Number(h.previous_amount).toFixed(2)}</span>
                                {' → '}
                                <span className="font-mono font-bold">{h.new_currency} {Number(h.new_amount).toFixed(2)}</span>
                                {typeof delta === 'number' && (
                                  <span className={`ml-2 text-xs font-bold ${isIncrease ? 'text-rose-700' : isDecrease ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                                    {isIncrease ? '+' : ''}{delta.toFixed(2)}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                          {h.changed_by_email && (
                            <div className="text-[10px] text-muted-foreground">by {h.changed_by_email}</div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
